import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  DataTable,
  Field,
  Select,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type { ComplianceFramework, FrameworkVersion } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { frameworksApi } from "../../lib/api";
import { GOVERNANCE_PERMISSIONS, GovPanel, GovernedAction } from "../../governance";
import { ContentPolicyBadge, ScopeBadge, VersionStatusBadge, contentPolicyNote } from "./labels";

interface CatalogueData {
  global: ComplianceFramework[];
  tenant: { framework: ComplianceFramework; versions: FrameworkVersion[] }[];
  adoptedFrom: Set<string>;
}

type CatalogueRow = ComplianceFramework & { adopted: boolean };

// Framework catalogue and tenant adoption (APP-004 R3 P1). Nothing on this
// screen is derived locally: scope, content policy and version status are the
// backend's own classifications.
export function GovFrameworks() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");

  const state = useAsync<CatalogueData>(async () => {
    const res = await frameworksApi.frameworks(orgId, query ? { search: query } : {});
    const frameworks = res.data.frameworks;
    const tenant = frameworks.filter((f) => f.scope === "tenant");
    const versions = await Promise.all(
      tenant.map(async (f) => ({
        framework: f,
        versions: (await frameworksApi.versions(orgId, f.id)).data.versions,
      })),
    );
    return {
      global: frameworks.filter((f) => f.scope === "global"),
      tenant: versions,
      adoptedFrom: new Set(
        tenant.map((f) => f.copiedFromFrameworkId).filter((id): id is string => Boolean(id)),
      ),
    };
  }, [orgId, query]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Frameworks</h1>
          <p>
            Adopt a framework from the Odingard catalogue or register your own, then map policies to its
            requirements.
          </p>
        </div>
        <RegisterFramework orgId={orgId} onDone={state.reload} />
      </div>

      <Card>
        <CardBody>
          <form
            className="filter-row"
            onSubmit={(e) => {
              e.preventDefault();
              setQuery(search.trim());
            }}
          >
            <Field label="Search frameworks">
              {(id) => (
                <TextInput
                  id={id}
                  value={search}
                  placeholder="Name or key, e.g. iso27001"
                  onChange={(e) => setSearch(e.target.value)}
                />
              )}
            </Field>
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>
        </CardBody>
      </Card>

      <GovPanel
        title="Global catalogue"
        state={state}
        loadingLabel="Loading frameworks…"
        isEmpty={(d) => d.global.length === 0}
        emptyTitle="No catalogue frameworks match"
        emptyDescription="Clear the search to see every framework Odingard publishes."
      >
        {(data) => (
          <>
            <p className="muted-note">
              Published by Odingard and read only. Adopting one copies its requirement hierarchy into your
              organization so you can map and verify against it.
            </p>
            <DataTable
              columns={[
                {
                  key: "name",
                  header: "Framework",
                  render: (f: CatalogueRow) => (
                    <>
                      <strong>{f.name}</strong>
                      <div className="muted-note">
                        {f.frameworkKey} · {f.authority}
                      </div>
                    </>
                  ),
                },
                { key: "scope", header: "Source", render: (f: CatalogueRow) => <ScopeBadge scope={f.scope} /> },
                {
                  key: "content",
                  header: "Content",
                  render: (f: CatalogueRow) => <ContentPolicyBadge policy={f.contentPolicy} />,
                },
                {
                  key: "adopt",
                  header: "",
                  render: (f: CatalogueRow) =>
                    f.adopted ? (
                      <Badge tone="success">Adopted</Badge>
                    ) : (
                      <GovernedAction
                        permission={GOVERNANCE_PERMISSIONS.frameworksManage}
                        label="Adopt"
                        title={`Adopt ${f.name}`}
                        description="A tenant copy of this framework and its requirement hierarchy is created in your organization."
                        consequence={contentPolicyNote(f.contentPolicy)}
                        confirmLabel="Adopt framework"
                        busyLabel="Adopting…"
                        onConfirm={() => frameworksApi.adoptFramework(orgId, f.id)}
                        onDone={state.reload}
                      />
                    ),
                },
              ]}
              rows={data.global.map(
                (f): CatalogueRow => ({ ...f, adopted: data.adoptedFrom.has(f.id) }),
              )}
              caption="Global framework catalogue"
            />
          </>
        )}
      </GovPanel>

      <GovPanel
        title="Your frameworks"
        state={state}
        loadingLabel="Loading your frameworks…"
        isEmpty={(d) => d.tenant.length === 0}
        emptyTitle="No frameworks adopted yet"
        emptyDescription="Adopt one from the catalogue above, or register a framework of your own."
      >
        {(data) => (
          <div className="stack">
            {data.tenant.map(({ framework, versions }) => (
              <div key={framework.id} className="gov-framework">
                <div className="gov-framework__head">
                  <div>
                    <strong>{framework.name}</strong>
                    <div className="muted-note">
                      {framework.frameworkKey}
                      {framework.authority ? ` · ${framework.authority}` : ""}
                    </div>
                  </div>
                  <div className="badge-row">
                    <ScopeBadge scope={framework.scope} />
                    <ContentPolicyBadge policy={framework.contentPolicy} />
                  </div>
                </div>
                {versions.length === 0 ? (
                  <p className="muted-note">No versions yet.</p>
                ) : (
                  <ul className="gov-version-list">
                    {versions.map((v) => (
                      <li key={v.id}>
                        <Link to={`/studio/governance/frameworks/${v.id}?frameworkId=${framework.id}`}>
                          {v.versionLabel}
                        </Link>{" "}
                        <VersionStatusBadge status={v.status} />
                        <span className="muted-note">
                          {" "}
                          {v.requirementCount} requirements
                          {v.effectiveDate ? ` · effective ${formatDate(v.effectiveDate)}` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <CreateVersion orgId={orgId} frameworkId={framework.id} onDone={state.reload} />
              </div>
            ))}
          </div>
        )}
      </GovPanel>
    </>
  );
}

function RegisterFramework({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [authority, setAuthority] = useState("");
  const [policy, setPolicy] = useState<ComplianceFramework["contentPolicy"]>("summary_permitted");

  return (
    <GovernedAction
      permission={GOVERNANCE_PERMISSIONS.frameworksManage}
      label="Register framework"
      title="Register a tenant framework"
      description="Registers a framework owned by your organization. Frameworks in the global catalogue are published by Odingard and cannot be created here."
      confirmLabel="Register"
      busyLabel="Registering…"
      triggerVariant="primary"
      triggerSize="md"
      onConfirm={() =>
        frameworksApi.registerFramework(orgId, {
          frameworkKey: key.trim(),
          name: name.trim(),
          authority: authority.trim(),
          contentPolicy: policy,
        })
      }
      onDone={() => {
        setKey("");
        setName("");
        setAuthority("");
        onDone();
      }}
    >
      <Field label="Framework key" help="Lowercase identifier, unique within your organization.">
        {(id) => <TextInput id={id} value={key} onChange={(e) => setKey(e.target.value)} />}
      </Field>
      <Field label="Name">
        {(id) => <TextInput id={id} value={name} onChange={(e) => setName(e.target.value)} />}
      </Field>
      <Field label="Authority" help="Who publishes it, if anyone.">
        {(id) => <TextInput id={id} value={authority} onChange={(e) => setAuthority(e.target.value)} />}
      </Field>
      <Field
        label="Content policy"
        help="Controls what requirement text the backend will store and return."
      >
        {(id) => (
          <Select
            id={id}
            value={policy}
            onChange={(e) => setPolicy(e.target.value as ComplianceFramework["contentPolicy"])}
          >
            <option value="identifier_only">Identifier only</option>
            <option value="summary_permitted">Odingard authored summary permitted</option>
            <option value="licensed_only">Licensed content only</option>
          </Select>
        )}
      </Field>
    </GovernedAction>
  );
}

function CreateVersion({
  orgId,
  frameworkId,
  onDone,
}: {
  orgId: string;
  frameworkId: string;
  onDone: () => void;
}) {
  const [label, setLabel] = useState("");
  const [effective, setEffective] = useState("");

  return (
    <GovernedAction
      permission={GOVERNANCE_PERMISSIONS.frameworksManage}
      label="Create version"
      title="Create a draft version"
      description="Draft versions can receive a requirement import. Activating one supersedes the framework's current active version."
      confirmLabel="Create draft"
      busyLabel="Creating…"
      onConfirm={() =>
        frameworksApi.createVersion(orgId, frameworkId, {
          versionLabel: label.trim(),
          ...(effective ? { effectiveDate: effective } : {}),
        })
      }
      onDone={() => {
        setLabel("");
        setEffective("");
        onDone();
      }}
    >
      <Field label="Version label">
        {(id) => <TextInput id={id} value={label} onChange={(e) => setLabel(e.target.value)} />}
      </Field>
      <Field label="Effective date" help="Optional.">
        {(id) => (
          <TextInput id={id} type="date" value={effective} onChange={(e) => setEffective(e.target.value)} />
        )}
      </Field>
    </GovernedAction>
  );
}
